import { Box } from '@chakra-ui/react';
import { forEachDiagnostic } from '@codemirror/lint';
import { EditorState } from '@codemirror/state';
import { EditorView, placeholder } from '@codemirror/view';
import { useEffect, useRef } from 'react';

import { autocompleteExtension } from './autocomplete-extension';
import { textChartEditorSetup } from './editor-setup';
import { handlebarsMarkdown } from './handlebars-lang';
import { linterExtension } from './linter-extension';
import { tooltipExtension } from './tooltip-extension';
import type { HelperError } from './utils/error-collector';
import type { DataContext } from './utils/handlebars-setup';

type Props = {
  initialValue: string;
  onChange: (value: string) => void;
  onLintIssues?: (hasErrors: boolean) => void;
  validate: (content: string) => Promise<HelperError[]>;
  dataContext: DataContext;
};

function hasLintErrors(view: EditorView) {
  let hasErrors = false;
  forEachDiagnostic(view.state, (diagnostic) => {
    if (diagnostic.severity === 'error') {
      hasErrors = true;
    }
  });
  return hasErrors;
}

export const CodeMirrorEditor = ({
  initialValue,
  onChange,
  onLintIssues,
  validate,
  dataContext,
}: Props) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  const onLintIssuesRef = useRef(onLintIssues);
  const validateRef = useRef(validate);
  const dataContextRef = useRef(dataContext);
  const lastLintStateRef = useRef<boolean | null>(null);

  onChangeRef.current = onChange;
  onLintIssuesRef.current = onLintIssues;
  validateRef.current = validate;
  dataContextRef.current = dataContext;

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!editorRef.current || typeof window === 'undefined') return;

    const state = EditorState.create({
      doc: initialValue,
      extensions: [
        textChartEditorSetup,
        EditorView.lineWrapping,
        EditorView.theme(
          {
            '&': {
              height: '100%',
            },
            '.cm-scroller': {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            },
            '.cm-content': {
              padding: '8px',
            },
            '.cm-tooltip-helper': {
              padding: '8px 12px',
              maxWidth: '420px',
              fontSize: '12px',
              lineHeight: '1.45',
              background: 'var(--chakra-colors-gray-50, #f7fafc)',
              border: '1px solid var(--chakra-colors-gray-200, #e2e8f0)',
              borderRadius: '6px',
            },
            '.cm-tooltip-helper-title': {
              fontWeight: '600',
              color: 'var(--chakra-colors-blue-700)',
            },
            '.cm-tooltip-helper-header': {
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              marginBottom: '8px',
            },
            '.cm-tooltip-helper-syntax': {
              display: 'inline-block',
              background: 'var(--chakra-colors-purple-50, #faf5ff)',
              color: 'var(--chakra-colors-purple-700)',
              border: '1px solid var(--chakra-colors-purple-200, #d6bcfa)',
              borderRadius: '4px',
              padding: '4px 6px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              whiteSpace: 'pre-wrap',
            },
            '.cm-tooltip-helper-body': {
              color: '#4a5568',
              marginBottom: '8px',
            },
            '.cm-tooltip-helper-examples-label': {
              fontWeight: '600',
              marginBottom: '4px',
            },
            '.cm-tooltip-helper-examples': {
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            },
            '.cm-tooltip-helper-example': {
              textAlign: 'left',
              border: '1px solid var(--chakra-colors-gray-200, #e2e8f0)',
              borderRadius: '4px',
              padding: '4px 6px',
              background: 'var(--chakra-colors-green-50, #f0fff4)',
              color: 'var(--chakra-colors-green-700)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '11px',
              cursor: 'pointer',
            },
          },
          { dark: false }
        ),
        placeholder('Enter your text content here...'),
        ...handlebarsMarkdown(),
        autocompleteExtension(() => dataContextRef.current),
        tooltipExtension(),
        linterExtension((content) => validateRef.current(content)),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            onChangeRef.current(update.state.doc.toString());
          }
          const hasErrors = hasLintErrors(update.view);
          if (hasErrors !== lastLintStateRef.current) {
            lastLintStateRef.current = hasErrors;
            onLintIssuesRef.current?.(hasErrors);
          }
        }),
      ],
    });

    const view = new EditorView({
      state,
      parent: editorRef.current,
    });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    if (view.hasFocus) return;

    const currentDoc = view.state.doc.toString();
    if (initialValue !== currentDoc) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: initialValue },
      });
    }
  }, [initialValue]);

  return (
    <Box
      ref={editorRef}
      data-testid="text-content-editor"
      height="100%"
      fontSize="sm"
      borderWidth="1px"
      borderRadius="md"
      overflow="hidden"
    />
  );
};
